import React, { useState, useEffect, useRef, Fragment } from "react";
import {
  Upload,
  RefreshCw,
  Trash2,
  Plus,
  X,
  Loader,
  Check,
  Download,
  ClipboardCopy,
  Languages,
  FileText,
  Image,
  Sparkles,
  Clock,
  AlertCircle,
  CheckCircle,
  XCircle,
  ChevronDown,
  ArrowRight,
  Zap,
  Brain,
  Gauge,
  Loader as Spinner,
  CheckIcon,
  ChevronLeft,
} from "lucide-react";
import { useAuth } from "../hooks/useAuth";
import { LANGS, MODELS } from "../data/translateMeta";
import { Listbox, Dialog, Transition } from "@headlessui/react";
import { ChevronUpDownIcon } from "@heroicons/react/20/solid";
import { useNavigate } from "react-router-dom";
import MainHeader from "../MainHeader";
import TGL from "../assets/tgl.png";
/********************** 공통 헬퍼 **************************/
const bufToBase64 = (file) =>
  new Promise((res, rej) => {
    const reader = new FileReader();
    reader.onload = () => {
      const u8 = new Uint8Array(reader.result);
      let bin = "";
      for (let b of u8) bin += String.fromCharCode(b);
      res(btoa(bin));
    };
    reader.onerror = () => rej(reader.error);
    reader.readAsArrayBuffer(file);
  });

/* API 응답 unwrap */
const unwrap = (p) =>
  Array.isArray(p) ? p : Array.isArray(p?.data) ? p.data : [];

export default function TranslatePanel() {
  const { api } = useAuth();
  const navigate = useNavigate();
  /******************** 상태 ********************/
  /* 즉시 번역 */
  const [directText, setDirectText] = useState("");
  const [directResult, setDirectResult] = useState("");
  const [directLoading, setDirectLoading] = useState(false);
  const [directDest, setDirectDest] = useState("ko");
  const [toast, setToast] = useState(false);
  /* 이미지 OCR */
  const [imgFiles, setImgFiles] = useState([]);
  const [imgDest, setImgDest] = useState("ko");
  const [imgLoading, setImgLoading] = useState(false);
  const [imgResult, setImgResult] = useState("");

  /* 스케줄 */
  const [waiting, setWaiting] = useState([]);
  const [completed, setCompleted] = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const compCursor = useRef(0);
  const compLoading = useRef(false);
  const fileInputRef = useRef(null);
  /* 스케줄 등록 모달 */
  const [modalOpen, setModalOpen] = useState(false);
  const [jobName, setJobName] = useState("");
  const [jobModel, setJobModel] = useState(Object.keys(MODELS)[1]);
  const [jobDest, setJobDest] = useState("ko");
  const [jobFile, setJobFile] = useState(null);
  const [creating, setCreating] = useState(false);

  /******************** 즉시 번역 ********************/
  const handleDirect = async () => {
    if (!directText.trim()) return;
    setDirectLoading(true);
    try {
      const { data } = await api.post("/tr/direct", {
        translate_text: directText,
        dest_lang: directDest,
      });
      setDirectResult(data.translated);
    } catch {
      alert("번역 실패");
    } finally {
      setDirectLoading(false);
    }
  };
  const copyAllImgResult = () => {
    navigator.clipboard.writeText(imgResult).then(() => {
      setToast(true);
      setTimeout(() => setToast(false), 800);
    });
  };

  const resetImgOcr = () => {
    setImgResult("");
    setImgFiles([]);
  };

  /******************** 이미지 OCR ********************/
  const handleImageChoose = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setImgFiles((prev) => [...prev, ...files]);
    e.target.value = "";
  };
  const removeImg = (name) =>
    setImgFiles((prev) => prev.filter((f) => f.name !== name));

  const clearImgs = () => setImgFiles([]);

  const handleImageTranslate = async () => {
    if (!imgFiles.length) return;
    setImgLoading(true);
    try {
      const images = await Promise.all(
        imgFiles.map(async (f) => {
          const ext = f.name.split(".").pop().toLowerCase();
          if (!["png", "jpg", "jpeg"].includes(ext))
            throw new Error("지원하지 않는 확장자");
          return { b64: await bufToBase64(f), ext };
        })
      );
      const { data } = await api.post("/tr/ocr/direct", {
        images,
        dest_lang: imgDest,
      });
      setImgResult(data.translated || "");
    } catch (e) {
      console.error(e);
      alert("이미지 번역 실패");
    } finally {
      setImgLoading(false);
    }
  };

  /******************** 스케줄 API ********************/
  const canCreate = waiting.length < 5 && jobName.trim() && jobFile;

  const handleCreate = async () => {
    if (!canCreate) return;
    setCreating(true);
    try {
      await api.post("/schedule/create", {
        job_name: jobName,
        file_name: jobFile.name.replace(/\.[^.]*/, ""),
        file_type: jobFile.name.toLowerCase().endsWith(".pdf") ? "pdf" : "txt",
        file_base64: await bufToBase64(jobFile),
        dest_lang: jobDest,
        model: jobModel,
      });
      compCursor.current = 0;
      setHasMore(true);
      await fetchLists(true);
      setJobName("");
      setJobFile(null);
      setModalOpen(false);
    } catch {
      alert("등록 실패");
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("삭제하시겠습니까?")) return;
    try {
      await api.post("/schedule/delete", { id });
      setWaiting((p) => p.filter((j) => j.id !== id));
      setCompleted((p) => p.filter((j) => j.id !== id));
    } catch {
      alert("삭제 실패");
    }
  };

  const fetchLists = async (reset = false) => {
    if (compLoading.current || (!hasMore && !reset)) return;
    setRefreshing(true);
    try {
      /* 대기중 */
      const wRes = await api.get("/schedule/list/wait");
      setWaiting(unwrap(wRes.data));

      /* 완료 */
      if (reset) {
        compCursor.current = 0;
        setHasMore(true);
      }
      compLoading.current = true;
      const cRes = await api.get("/schedule/list/clear", {
        params: { last_cursor: compCursor.current },
      });
      const arr = unwrap(cRes.data);
      if (arr.length) {
        setCompleted((prev) => (reset ? arr : prev.concat(arr)));
        compCursor.current =
          arr[arr.length - 1]?.latency_ns ?? compCursor.current;
      } else {
        setHasMore(false);
      }
      compLoading.current = false;
    } catch (e) {
      console.error(e);
      compLoading.current = false;
    } finally {
      setRefreshing(false);
    }
  };

  const loadMore = () => fetchLists();

  /* 최초 & 1분 주기 갱신 */
  useEffect(() => {
    fetchLists(true);
    const iv = setInterval(() => fetchLists(true), 60000);
    return () => clearInterval(iv);
  }, []);

  /******************** 기타 헬퍼 ********************/
  const handleDownload = (job) => {
    if (!job.translate_output) return;
    const blob = new Blob([job.translate_output], {
      type: "text/plain;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${job.job_name}.txt`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const copyTxt = (t) => navigator.clipboard.writeText(t);

  /******************** JSX ********************/
  return (
    <>
      <MainHeader />
      <div className="pt-10 min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-6">
        {/* 헤더 */}
        <div className="w-full mb-8">
          <div className="flex items-center gap-4 mb-2">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-purple-600 rounded-xl flex items-center justify-center">
              <Languages className="w-7 h-7 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-gray-800">
              문서 번역 시스템
            </h1>
          </div>
          <p className="text-gray-600 ml-16">
            TGL 엔진을 활용한 고품질 번역 서비스
          </p>
        </div>

        <div className="w-full space-y-6">
          {/* 1) 즉시 번역 */}
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-6 text-white">
              <div className="flex items-center gap-3">
                <Zap className="w-6 h-6" />
                <h2 className="text-2xl font-semibold">즉시 번역</h2>
              </div>
              <p className="mt-2 text-blue-100">
                텍스트를 입력하면 바로 번역 결과를 확인할 수 있습니다
              </p>
            </div>

            <div className="p-6">
              <div className="grid md:grid-cols-2 gap-6">
                {/* 입력 */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-gray-700">
                      원본 텍스트
                    </label>
                    <div className="text-sm text-gray-500">
                      {directText.length} 자
                    </div>
                  </div>
                  <textarea
                    rows={8}
                    value={directText}
                    onChange={(e) => setDirectText(e.target.value)}
                    onFocus={(e) => e.target.select()}
                    className="w-full border-2 border-gray-200 rounded-xl p-4 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all resize-none"
                    placeholder="번역할 텍스트를 입력하세요..."
                  />
                  <div className="flex items-center gap-3">
                    <select
                      value={directDest}
                      onChange={(e) => setDirectDest(e.target.value)}
                      className="flex-1 border-2 border-gray-200 rounded-lg px-4 py-2 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all"
                    >
                      {LANGS.filter((l) => l.code !== "auto").map((l) => (
                        <option key={l.code} value={l.code}>
                          {l.label}
                        </option>
                      ))}
                    </select>
                    <button
                      onClick={handleDirect}
                      disabled={directLoading || !directText.trim()}
                      className="px-6 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-lg transform hover:-translate-y-0.5 transition-all flex items-center gap-2"
                    >
                      {directLoading ? (
                        <>
                          <Loader className="w-5 h-5 animate-spin" />
                          번역 중...
                        </>
                      ) : (
                        <>
                          <ArrowRight className="w-5 h-5" />
                          번역하기
                        </>
                      )}
                    </button>
                  </div>
                </div>

                {/* 결과 */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-gray-700">
                      번역 결과
                    </label>
                    {directResult && (
                      <button
                        onClick={() => copyTxt(directResult)}
                        className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
                      >
                        <ClipboardCopy className="w-4 h-4" />
                        복사
                      </button>
                    )}
                  </div>
                  <div className="border-2 border-gray-200 rounded-xl p-4 min-h-[200px] bg-gray-50">
                    {directResult ? (
                      <p className="whitespace-pre-wrap">{directResult}</p>
                    ) : (
                      <p className="text-gray-400 italic">
                        번역 결과가 여기에 표시됩니다
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 2) 이미지 OCR 번역 */}
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="bg-gradient-to-r from-purple-600 to-pink-600 p-6 text-white">
              <div className="flex items-center gap-3">
                <Image className="w-6 h-6" />
                <h2 className="text-2xl font-semibold">이미지 OCR 번역</h2>
              </div>
              <p className="mt-2 text-purple-100">
                이미지 속 텍스트를 자동으로 인식하여 번역합니다
              </p>
            </div>

            <div className="p-6">
              {!imgResult ? (
                <div className="space-y-4">
                  {/* 드롭존 */}
                  <label className="relative block">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/png,image/jpeg"
                      multiple
                      hidden
                      onChange={handleImageChoose}
                    />
                    <div className="border-2 border-dashed border-purple-300 rounded-xl p-12 text-center cursor-pointer hover:border-purple-400 hover:bg-purple-50 transition-all">
                      <Image className="w-16 h-16 text-purple-400 mx-auto mb-4" />
                      <p className="text-lg font-medium text-gray-700">
                        이미지를 드래그하거나 클릭하여 업로드
                      </p>
                      <p className="text-sm text-gray-500 mt-2">
                        PNG, JPG 형식 지원 · 최대 100장
                      </p>
                    </div>
                  </label>

                  {/* 썸네일 */}
                  {imgFiles.length > 0 && (
                    <div className="space-y-6">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-medium text-gray-700">
                          업로드된 이미지 ({imgFiles.length})
                        </p>
                        <button
                          onClick={clearImgs}
                          className="text-sm text-red-600 hover:text-red-700"
                        >
                          전체 삭제
                        </button>
                      </div>
                      <div className="flex gap-3 overflow-x-auto pb-2">
                        {imgFiles.map((f) => (
                          <div
                            key={f.name}
                            className="relative group flex-shrink-0"
                          >
                            <img
                              src={URL.createObjectURL(f)}
                              alt={f.name}
                              className="h-24 w-24 object-cover rounded-lg border-2 border-gray-200"
                            />
                            <button
                              onClick={() => removeImg(f.name)}
                              className="absolute -top-0 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* 언어 선택 & 번역 버튼 */}
                  <div className="flex items-center gap-3">
                    <select
                      value={imgDest}
                      onChange={(e) => setImgDest(e.target.value)}
                      className="flex-1 border-2 border-gray-200 rounded-lg px-4 py-2 focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition-all"
                    >
                      {LANGS.filter((l) => l.code !== "auto").map((l) => (
                        <option key={l.code} value={l.code}>
                          {l.label}
                        </option>
                      ))}
                    </select>
                    <button
                      onClick={handleImageTranslate}
                      disabled={imgLoading || !imgFiles.length}
                      className="px-6 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-lg transform hover:-translate-y-0.5 transition-all flex items-center gap-2"
                    >
                      {imgLoading ? (
                        <>
                          <Loader className="w-5 h-5 animate-spin" />
                          번역 중...
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-5 h-5" />
                          이미지 번역
                        </>
                      )}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-medium text-gray-700">
                      번역 결과
                    </h3>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={copyAllImgResult}
                        className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                      >
                        <ClipboardCopy className="w-4 h-4" />
                        전체 복사
                      </button>
                      <button
                        onClick={resetImgOcr}
                        className="px-4 py-2 bg-gray-200 text-gray-700 text-sm rounded-lg hover:bg-gray-300 transition-colors"
                      >
                        다시 업로드
                      </button>
                    </div>
                  </div>
                  <div className="border-2 border-gray-200 rounded-xl p-4 min-h-[300px] bg-gray-50 whitespace-pre-wrap">
                    {imgResult}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* 3) 스케줄 등록 */}
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="bg-gradient-to-r from-indigo-600 to-blue-600 p-6 text-white">
              <div className="flex items-center gap-3">
                <FileText className="w-6 h-6" />
                <h2 className="text-2xl font-semibold">번역 스케줄 등록</h2>
              </div>
              <p className="mt-2 text-indigo-100">
                대용량 PDF/TXT 파일을 백그라운드에서 번역합니다
              </p>
            </div>

            <div className="p-6">
              <button
                onClick={() => setModalOpen(true)}
                className="w-full group"
              >
                <div className="border-2 border-dashed border-indigo-300 rounded-xl p-12 text-center hover:border-indigo-400 hover:bg-indigo-50 transition-all">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-indigo-100 rounded-full mb-4 group-hover:scale-110 transition-transform">
                    <Plus className="w-8 h-8 text-indigo-600" />
                  </div>
                  <p className="text-lg font-medium text-gray-700">
                    새로운 번역 스케줄 등록
                  </p>
                  <p className="text-sm text-gray-500 mt-2">
                    클릭하여 파일을 업로드하고 번역을 예약하세요
                  </p>
                </div>
              </button>

              <div className="mt-4 grid grid-cols-3 gap-4 text-center">
                <div className="bg-blue-50 rounded-lg p-4">
                  <Clock className="w-6 h-6 text-blue-600 mx-auto mb-2" />
                  <p className="text-sm text-gray-700">현재 대기열</p>
                  <p className="text-2xl font-bold text-blue-600">
                    {waiting.length}/5
                  </p>
                </div>
                <div className="bg-green-50 rounded-lg p-4">
                  <CheckCircle className="w-6 h-6 text-green-600 mx-auto mb-2" />
                  <p className="text-sm text-gray-700">완료된 작업</p>
                  <p className="text-2xl font-bold text-green-600">
                    {completed.length}
                  </p>
                </div>
                <div className="bg-purple-50 rounded-lg p-4">
                  <Gauge className="w-6 h-6 text-purple-600 mx-auto mb-2" />
                  <p className="text-sm text-gray-700">처리 상태</p>
                  <p className="text-2xl font-bold text-purple-600">정상</p>
                </div>
              </div>
            </div>
          </div>

          {/* 4) 작업 목록 */}
          <div className="grid md:grid-cols-2 gap-6">
            {/* 대기중 */}
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
              <div className="bg-gradient-to-r from-amber-500 to-orange-500 p-4 text-white flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Clock className="w-5 h-5" />
                  <h3 className="text-lg font-semibold">작업 대기중</h3>
                </div>
                <button
                  onClick={() => fetchLists(true)}
                  disabled={refreshing}
                  className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                >
                  <RefreshCw
                    className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`}
                  />
                </button>
              </div>

              <div className="p-4 space-y-3 max-h-96 overflow-y-auto">
                {waiting.length === 0 ? (
                  <div className="text-center py-8 text-gray-400">
                    <Clock className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>대기중인 작업이 없습니다</p>
                  </div>
                ) : (
                  waiting.map((j) => (
                    <div
                      key={j.id}
                      className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-center justify-between"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-2 h-2 bg-amber-500 rounded-full animate-pulse"></div>
                        <span className="font-medium text-gray-800 truncate max-w-[200px]">
                          {j.job_name}
                        </span>
                      </div>
                      <button
                        onClick={() => handleDelete(j.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* 완료 */}
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
              <div className="bg-gradient-to-r from-green-500 to-emerald-500 p-4 text-white flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5" />
                  <h3 className="text-lg font-semibold">작업 완료</h3>
                </div>
                <button
                  onClick={() => fetchLists(true)}
                  disabled={refreshing}
                  className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                >
                  <RefreshCw
                    className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`}
                  />
                </button>
              </div>

              <div className="p-4 space-y-3 max-h-96 overflow-y-auto">
                {completed.length === 0 ? (
                  <div className="text-center py-8 text-gray-400">
                    <CheckCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>완료된 작업이 없습니다</p>
                  </div>
                ) : (
                  <>
                    {completed.map((j) => {
                      const ok = j.scheduled_process === "clear";
                      const fail = j.scheduled_process === "failed";
                      return (
                        <div
                          key={j.id}
                          className={`${
                            ok
                              ? "bg-green-50 border-green-200"
                              : fail
                              ? "bg-red-50 border-red-200"
                              : "bg-gray-50 border-gray-200"
                          } border rounded-lg p-4 flex items-center justify-between`}
                        >
                          <div className="flex items-center gap-3">
                            {ok ? (
                              <CheckCircle className="w-5 h-5 text-green-600" />
                            ) : fail ? (
                              <XCircle className="w-5 h-5 text-red-600" />
                            ) : (
                              <Clock className="w-5 h-5 text-gray-600" />
                            )}
                            <span className="font-medium text-gray-800 truncate max-w-[200px]">
                              {j.job_name}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            {ok && (
                              <button
                                onClick={() => handleDownload(j)}
                                className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                              >
                                <Download className="w-4 h-4" />
                              </button>
                            )}
                            <button
                              onClick={() => handleDelete(j.id)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      );
                    })}

                    {hasMore && (
                      <button
                        onClick={loadMore}
                        disabled={compLoading.current}
                        className="w-full py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center justify-center gap-2"
                      >
                        {compLoading.current ? (
                          <>
                            <Loader className="w-4 h-4 animate-spin" />
                            불러오는 중...
                          </>
                        ) : (
                          <>
                            <ChevronDown className="w-4 h-4" />
                            더보기
                          </>
                        )}
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 스케줄 등록 모달 */}
      <Transition appear show={modalOpen} as={Fragment}>
        <Dialog
          as="div"
          className="fixed inset-0 z-50 overflow-auto bg-black/30 backdrop-blur-sm flex items-center justify-center p-4"
          onClose={() => setModalOpen(false)}
        >
          <Dialog.Panel className="bg-white rounded-2xl max-w-xl w-full p-6 shadow-2xl">
            {/* 헤더 */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-blue-500 rounded-xl flex items-center justify-center">
                  <FileText className="w-6 h-6 text-white" />
                </div>
                <Dialog.Title className="text-2xl font-bold text-gray-800">
                  번역 스케줄 등록
                </Dialog.Title>
              </div>
              <button
                onClick={() => setModalOpen(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="space-y-4">
              {/* 안내 */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-gray-700">
                    PDF/TXT 파일은 처리 시간이 길 수 있습니다. 스케줄로 등록해
                    두고 나중에 결과를 받아보세요!
                  </p>
                </div>
              </div>

              {/* 작업 이름 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  작업 이름
                </label>
                <input
                  type="text"
                  placeholder="예: 상반기 결산 보고서 번역"
                  value={jobName}
                  onChange={(e) => setJobName(e.target.value)}
                  className="w-full border-2 border-gray-200 rounded-lg px-4 py-2 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all"
                />
              </div>

              {/* 모델 선택 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  번역 모델
                </label>
                <Listbox value={jobModel} onChange={setJobModel}>
                  {({ open }) => (
                    <div className="relative">
                      <Listbox.Button className="w-full border-2 border-gray-200 rounded-lg px-4 py-2 flex items-center justify-between hover:border-gray-300 transition-colors">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-500 rounded-lg flex items-center justify-center">
                            <Brain className="w-5 h-5 text-white" />
                          </div>
                          <div className="text-left">
                            <p className="font-medium text-gray-800">
                              {jobModel}
                            </p>
                            <p className="text-xs text-gray-500">
                              {MODELS[jobModel].desc}
                            </p>
                          </div>
                        </div>
                        <ChevronUpDownIcon className="w-5 h-5 text-gray-400" />
                      </Listbox.Button>
                      <Listbox.Options className="absolute z-10 mt-2 w-full bg-white border border-gray-200 rounded-lg shadow-lg py-1 max-h-60 overflow-auto">
                        {Object.entries(MODELS).map(([key, cfg]) => (
                          <Listbox.Option
                            key={key}
                            value={key}
                            className={({ active }) =>
                              `px-4 py-3 cursor-pointer ${
                                active ? "bg-blue-50" : ""
                              }`
                            }
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-500 rounded-lg flex items-center justify-center">
                                <Brain className="w-5 h-5 text-white" />
                              </div>
                              <div className="flex-1">
                                <p className="font-medium text-gray-800">
                                  {key}
                                </p>
                                <p className="text-xs text-gray-500">
                                  {cfg.desc}
                                </p>
                              </div>
                              {jobModel === key && (
                                <Check className="w-5 h-5 text-blue-600" />
                              )}
                            </div>
                          </Listbox.Option>
                        ))}
                      </Listbox.Options>
                    </div>
                  )}
                </Listbox>
              </div>

              {/* 언어 선택 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  번역 대상 언어
                </label>
                <select
                  value={jobDest}
                  onChange={(e) => setJobDest(e.target.value)}
                  className="w-full border-2 border-gray-200 rounded-lg px-4 py-2 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all"
                >
                  {LANGS.filter((l) => l.code !== "auto").map((l) => (
                    <option key={l.code} value={l.code}>
                      {l.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* 파일 업로드 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  파일 업로드
                </label>
                <label className="block">
                  <input
                    type="file"
                    accept=".pdf,.txt"
                    hidden
                    onChange={(e) => setJobFile(e.target.files?.[0] || null)}
                  />
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-gray-400 hover:bg-gray-50 transition-all">
                    {jobFile ? (
                      <div className="flex items-center justify-center gap-3">
                        <FileText className="w-8 h-8 text-blue-600" />
                        <p className="font-medium text-gray-800">
                          {jobFile.name}
                        </p>
                      </div>
                    ) : (
                      <>
                        <Upload className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                        <p className="text-gray-600">
                          PDF/TXT 파일을 업로드해주세요
                        </p>
                        <p className="text-sm text-gray-500 mt-1">
                          최대 500페이지
                        </p>
                      </>
                    )}
                  </div>
                </label>
              </div>

              {/* 버튼 */}
              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => setModalOpen(false)}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  취소
                </button>
                <button
                  onClick={handleCreate}
                  disabled={!canCreate || creating}
                  className="px-6 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-lg transform hover:-translate-y-0.5 transition-all flex items-center gap-2"
                >
                  {creating ? (
                    <>
                      <Spinner className="w-5 h-5 animate-spin" />
                      등록 중...
                    </>
                  ) : (
                    <>
                      <CheckIcon className="w-5 h-5" />
                      등록하기
                    </>
                  )}
                </button>
              </div>
            </div>
          </Dialog.Panel>
        </Dialog>
      </Transition>

      {/* 토스트 */}
      {toast && (
        <div className="fixed bottom-6 right-6 bg-gray-800 text-white px-6 py-3 rounded-lg shadow-lg flex items-center gap-2 animate-slideIn">
          <CheckCircle className="w-5 h-5" />
          복사되었습니다!
        </div>
      )}
    </>
  );
}
